import {action, computed, observable, runInAction} from 'mobx';
import {EventEmitter} from 'events';
import {getRootStore} from '../../../../app.bootstrap';
import {IWifiAnalyzerResults} from '../../../../services/AngularServices/WifiAnalyzerService';
import isEmpty from 'lodash/isEmpty';
import {SCAN_TYPE} from '@techsee/techsee-common/lib/constants/scan.constants';
import {getWifiAnalyzerScanRecordId, normalizeWifiAnalyzerHistoryList} from '../../wifi-scan-history.utils';

export enum WifiScanHistoryEvents {
    OPEN_WIFI_SCAN = 'OPEN_WIFI_SCAN',
    OPEN_VISUAL_SCAN = 'OPEN_VISUAL_SCAN',
    APPLY_RESULTS = 'APPLY_RESULTS',
    UPDATE_SCREEN = 'UPDATE_SCREEN'
}

export interface IWifiScanHistoryController {
    readonly translate: (key: string, values?: Record<string, any>) => string;
    readonly useNewInterface: boolean;
    readonly dir: string;
    _scanId: string;
    sectionOpen: boolean;
    shouldShow: boolean;
    isEmpty: boolean;
    count: number;
    wifiScanHistoryData: IWifiAnalyzerResults[];
    wifiScanTranslation: string;
    toggleSection(): void;
    onClickWifiScanHistoryButton(wifiScanId: string): void;
    updateWifiScanHistory(
        refresh: boolean,
        accountId: string,
        userId: string,
        wifiScans?: IWifiAnalyzerResults[]
    ): Promise<void>;
    applyWifiScanResults(results: IWifiAnalyzerResults, shouldShow?: boolean): void;
    handleWifiScanResults(data: IWifiAnalyzerResults): void;
}

export class WifiScanHistoryController extends EventEmitter implements IWifiScanHistoryController {
    @observable private _sectionOpen: boolean = false;
    @observable private _wifiScanHistoryData: IWifiAnalyzerResults[] = [];
    @observable private _customerWifiScanHistoryLoaded: boolean = false;

    public readonly translate: (key: string, values?: Record<string, any>) => string;
    public readonly useNewInterface: boolean;
    public readonly dir: string;
    public readonly wifiScanTranslation: string;

    @observable private _isScreenMirroring: boolean;
    @observable private _isVideoApplicationMode: boolean;
    private readonly _enableAgentWifiScanHistory: boolean;
    private readonly _shouldShowWifiScanHistoryInAllModes: boolean;
    private readonly _libraryTaggingMode: boolean;
    public readonly _scanId: string;
    private readonly _wifiAnalyzerService: any;

    constructor(
        useNewInterface: boolean,
        dir: string,
        isScreenMirroring: boolean,
        isVideoApplicationMode: boolean,
        enableAgentWifiScanHistory: boolean,
        shouldShowWifiScanHistoryInAllModes: boolean,
        libraryTaggingMode: boolean,
        accountId: string,
        roomId: string,
        scanId: string
    ) {
        super();
        const rootStore = getRootStore();
        this.translate = rootStore.localizationService.translate;
        this.useNewInterface = useNewInterface;
        this.dir = dir;
        this.wifiScanTranslation = this.translate('REACT.WIFI_ANALYZER.WIFI_SCAN');
        this._isScreenMirroring = isScreenMirroring;
        this._isVideoApplicationMode = isVideoApplicationMode;
        this._enableAgentWifiScanHistory = enableAgentWifiScanHistory;
        this._shouldShowWifiScanHistoryInAllModes = shouldShowWifiScanHistoryInAllModes;
        this._libraryTaggingMode = libraryTaggingMode;
        this._scanId = scanId;
        this._wifiAnalyzerService = rootStore.wifiAnalyzerService;

        // Fetch initial WiFi scan data if feature is enabled
        if (this.enableAgentWifiScanHistory && accountId && roomId) {
            this.updateWifiScanHistory(false, accountId, roomId).then();
        }
    }

    @computed
    get sectionOpen(): boolean {
        return this._sectionOpen;
    }

    set sectionOpen(value: boolean) {
        runInAction(() => {
            this._sectionOpen = value;
        });
    }

    @computed
    get wifiScanHistoryData(): IWifiAnalyzerResults[] {
        return [...this._wifiScanHistoryData];
    }

    set wifiScanHistoryData(value: IWifiAnalyzerResults[]) {
        runInAction(() => {
            this._wifiScanHistoryData = value || [];
        });
    }

    @computed
    get shouldShow(): boolean {
        const isWifiScanHistoryDisplay =
            (this._isScreenMirroring || this._isVideoApplicationMode) && this._enableAgentWifiScanHistory;

        return (
            (isWifiScanHistoryDisplay && !this._libraryTaggingMode) ||
            !!this._scanId ||
            this._shouldShowWifiScanHistoryInAllModes
        );
    }

    @computed
    get isEmpty(): boolean {
        return !this.wifiScanHistoryData || this.wifiScanHistoryData.length === 0;
    }

    @computed
    get count(): number {
        return this.wifiScanHistoryData?.length || 0;
    }

    get enableAgentWifiScanHistory(): boolean {
        return this._enableAgentWifiScanHistory;
    }

    @action
    toggleSection = (): void => {
        this._sectionOpen = !this._sectionOpen;
    };

    onClickWifiScanHistoryButton = (wifiScanId: string): void => {
        const scan = this.wifiScanHistoryData.find((s) => getWifiAnalyzerScanRecordId(s) === wifiScanId);
        if (scan) {
            this.applyWifiScanResults(scan, true);
        }
        this.emit(WifiScanHistoryEvents.OPEN_WIFI_SCAN, wifiScanId);
    };

    // WiFi scan methods (non-AR scans)
    @action
    setWifiScanHistoryData = (data: IWifiAnalyzerResults[]): void => {
        const wifiScansOnly = (data || []).filter((scan) => scan.scanDetails?.scanType !== SCAN_TYPE.ar);
        this._wifiScanHistoryData = normalizeWifiAnalyzerHistoryList(wifiScansOnly);
    };

    @action
    pushWifiScanData = (data: IWifiAnalyzerResults): void => {
        if (!data || data.scanDetails?.scanType === SCAN_TYPE.ar) {
            return;
        }
        const combined = [data, ...(this._wifiScanHistoryData || [])];
        this._wifiScanHistoryData = normalizeWifiAnalyzerHistoryList(combined);
    };

    // Update WiFi scan history - accepts filtered WiFi scans from main controller
    async updateWifiScanHistory(
        refresh: boolean,
        accountId: string,
        userId: string,
        wifiScans?: IWifiAnalyzerResults[]
    ): Promise<void> {
        if (refresh || !this._customerWifiScanHistoryLoaded) {
            runInAction(() => {
                this._customerWifiScanHistoryLoaded = true;
            });

            if (wifiScans && wifiScans.length > 0) {
                this.setWifiScanHistoryData(wifiScans);

                const lastWifiScan = this._wifiScanHistoryData[0];
                if (this._enableAgentWifiScanHistory && lastWifiScan) {
                    this.emit(WifiScanHistoryEvents.APPLY_RESULTS, lastWifiScan);
                }
            }

            runInAction(() => {
                this._customerWifiScanHistoryLoaded = false;
            });
        }
    }

    // Apply WiFi scan results
    applyWifiScanResults = (results: IWifiAnalyzerResults, shouldShow: boolean = false): void => {
        if (!results) {
            return;
        }

        // Emit event for main controller to handle
        this.emit(WifiScanHistoryEvents.APPLY_RESULTS, results, shouldShow);

        if (shouldShow && this._enableAgentWifiScanHistory && results.wifiConnectionInfo) {
            this.emit(WifiScanHistoryEvents.UPDATE_SCREEN);
        }
    };

    // Handle new WiFi scan results from socket
    handleWifiScanResults = (data: IWifiAnalyzerResults): void => {
        if (!isEmpty(data)) {
            if (data.scanDetails?.scanType !== SCAN_TYPE.ar) {
                this.applyWifiScanResults(data, true);
                this.pushWifiScanData(data);
            }
        }
    };

    @action
    setIsScreenMirroring = (isScreenMirroring: boolean): void => {
        this._isScreenMirroring = isScreenMirroring;
    };

    @action
    setIsVideoApplicationMode = (isVideoApplicationMode: boolean): void => {
        this._isVideoApplicationMode = isVideoApplicationMode;
    };

    dispose(): void {
        this.removeAllListeners();
    }
}
